import * as React from 'react';
import { useEffect } from 'react';
import {
    Datagrid,
    DateField,
    ExportButton,
    FilterButton,
    FilterForm,
    FunctionField,
    List,
    ReferenceField,
    SelectArrayInput,
    TextField,
    TextInput,
    TopToolbar,
    useRedirect,
    useRefresh,
} from 'react-admin';
import { getRequestStatusName } from '../enum-translates';
import { Chip } from '@material-ui/core';
import { getStatusColor } from '../common/status-history';
import { Box, Button, Tooltip } from '@mui/material';
import { getJwtData } from '../auth/authProvider';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import { UniqueRequest } from '../models/unique-request';
import { WorkStatusType } from '../models/work-status';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { UniqueRequestTypeChip } from './unique-request-type';
import { useTheme } from '@mui/material/styles';

const filters = [
    <TextInput
        source="query"
        label={'Keresés'}
        name={'query'}
        fullWidth
        alwaysOn
    />,
    <SelectArrayInput
        source="type"
        label="Típus"
        choices={[
            { id: 'Document', name: 'Változásbejelentés' },
            { id: 'DebtFree', name: 'Tartozásmentességi igazolás igénylés' },
            { id: 'Unique', name: 'Egyedi' },
        ]}
    />,
    <TextInput
        source="userQuery"
        label={'Név / Email / Telefonszám'}
        name={'userQuery'}
        fullWidth
    />,
    <SelectArrayInput
        source="status"
        label={'Státusz'}
        choices={[
            { id: 'Free', name: 'Foglalható' },
            { id: 'InProgress', name: 'Folyamatban' },
            { id: 'UnderDuty', name: 'Feldolgozás alatt' },
            { id: 'Closed', name: 'Lezárva' },
        ]}
    />,
];

const UniqueRequestFilters = () => {
    const redirect = useRedirect();

    return (
        <Box display={'flex'} alignItems={'flex-end'}>
            <Button
                sx={{ mb: 0.5, mr: 1, minHeight: 40 }}
                variant={'contained'}
                onClick={() => redirect('list', 'unique-request-template')}
                startIcon={<EditNoteOutlinedIcon />}
            >
                Üzenet sablonok
            </Button>
            <FilterForm filters={filters} style={{ flex: '0 1 auto' }} />
        </Box>
    );
};

const UniqueRequestListActions = () => (
    <TopToolbar>
        <FilterButton filters={filters} />
        <ExportButton />
    </TopToolbar>
);

export const UniqueRequestList = () => {
    const theme = useTheme();
    const user = getJwtData();
    const redirect = useRedirect();
    const refresh = useRefresh();

    useEffect(() => {
        const id = setInterval(() => {
            refresh();
        }, 25000);
        return () => clearInterval(id);
    }, []);

    return (
        <List
            title={'Bejelentések'}
            sort={{ field: 'CreatedAt', order: 'DESC' }}
            filters={<UniqueRequestFilters />}
            actions={<UniqueRequestListActions />}
        >
            <Datagrid bulkActionButtons={false}>
                <TextField source={'caseNumber'} label={'Iktatószám'} />
                <FunctionField
                    label="Típus"
                    render={(record: UniqueRequest) => (
                        <UniqueRequestTypeChip type={record.type} />
                    )}
                />
                <ReferenceField
                    source="user.id"
                    reference="web-user"
                    link="show"
                    label={'Felhasználó'}
                    sortable={false}
                    emptyText="Törölt felhasználó"
                >
                    <TextField source="name" />
                </ReferenceField>
                <TextField
                    source={'subject'}
                    label={'Tárgy'}
                    sortable={false}
                />
                <FunctionField
                    label="Státusz"
                    render={(record: UniqueRequest) => (
                        <Box display={'flex'} alignItems={'center'}>
                            <Tooltip
                                placement={'top'}
                                title={
                                    record.status.admin !== null
                                        ? `${record.status.admin.name} - ${record.status.lastModified.substring(0, 10)} ${record.status.lastModified.substring(11, 19)}`
                                        : ''
                                }
                                arrow
                            >
                                <Chip
                                    style={{
                                        backgroundColor: getStatusColor(
                                            theme,
                                            record.status.status
                                        ),
                                        color: '#FFF',
                                    }}
                                    label={getRequestStatusName(
                                        record.status.status
                                    )}
                                />
                            </Tooltip>
                            {record.unanswered &&
                                [
                                    WorkStatusType.InProgress,
                                    WorkStatusType.UnderDuty,
                                ].includes(record.status.status) && (
                                    <Tooltip
                                        title={'Válaszra vár!'}
                                        style={{ marginLeft: 7 }}
                                    >
                                        <AnnouncementOutlinedIcon
                                            color={'error'}
                                        />
                                    </Tooltip>
                                )}
                        </Box>
                    )}
                />
                <DateField
                    source="createdAt"
                    locales={['hu-HU']}
                    label={'Létrehozva'}
                    showTime
                />
                <FunctionField
                    label=""
                    render={(record: UniqueRequest) => (
                        <Button
                            variant={'contained'}
                            disabled={
                                record.status.status ===
                                    WorkStatusType.InProgress &&
                                record.status.admin.id !== user.userId
                            }
                            onClick={() => {
                                redirect('show', 'unique-request', record.id);
                            }}
                        >
                            Szerkesztés
                        </Button>
                    )}
                />
            </Datagrid>
        </List>
    );
};
